/**
 * Design tokens hook — provides programmatic access to CSS custom properties.
 *
 * Use this when you need theme values in JS
 * (e.g., for canvas drawing, computed styles, or conditional logic).
 *
 * For styling, prefer CSS custom properties directly in CSS/CSS Modules.
 */

/** Read a CSS custom property value from the document root */
const getCssVar = (name: string): string =>
  globalThis.document ? getComputedStyle(document.documentElement).getPropertyValue(name).trim() : ''

/** Breakpoint values in pixels */
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const

/** Max-width values (just under each breakpoint) for `max-width` media queries */
export const breakpointsDown = {
  sm: 599.95,
  md: 899.95,
  lg: 1199.95,
  xl: 1535.95,
} as const

export type Breakpoint = keyof typeof breakpoints

/** Spacing multiplier — matches MUI's 8px grid */
export const spacing = (factor = 1): string => (factor === 1 ? '8px' : `${factor * 8}px`)

export interface DesignTokens {
  palette: {
    primary: { main: string; light: string; dark: string; contrastText: string }
    secondary: { main: string; light: string; dark: string; contrastText: string }
    error: { main: string; light: string; dark: string; contrastText: string }
    warning: { main: string; light: string; dark: string; contrastText: string }
    info: { main: string; light: string; dark: string; contrastText: string }
    success: { main: string; light: string; dark: string; contrastText: string }
    text: { primary: string; secondary: string; disabled: string }
    action: {
      active: string
      hover: string
      selected: string
      disabled: string
      disabledBackground: string
      focus: string
    }
    background: { default: string; paper: string }
    divider: string
    layers: string
    grey: Record<string, string>
  }
  notes: {
    main: string
    outOfScale: string
    inScale: string
    root: string
  }
  spacing: typeof spacing
  breakpoints: typeof breakpoints
  shape: { borderRadius: string }
}

const paletteColor = (name: string) => ({
  main: getCssVar(`--tuna-palette-${name}-main`),
  light: getCssVar(`--tuna-palette-${name}-light`),
  dark: getCssVar(`--tuna-palette-${name}-dark`),
  contrastText: getCssVar(`--tuna-palette-${name}-contrastText`),
})

/** Get all design tokens by reading current CSS custom property values */
export const getDesignTokens = (): DesignTokens => ({
  palette: {
    primary: paletteColor('primary'),
    secondary: paletteColor('secondary'),
    error: paletteColor('error'),
    warning: paletteColor('warning'),
    info: paletteColor('info'),
    success: paletteColor('success'),
    text: {
      primary: getCssVar('--tuna-palette-text-primary'),
      secondary: getCssVar('--tuna-palette-text-secondary'),
      disabled: getCssVar('--tuna-palette-text-disabled'),
    },
    action: {
      active: getCssVar('--tuna-palette-action-active'),
      hover: getCssVar('--tuna-palette-action-hover'),
      selected: getCssVar('--tuna-palette-action-selected'),
      disabled: getCssVar('--tuna-palette-action-disabled'),
      disabledBackground: getCssVar('--tuna-palette-action-disabledBackground'),
      focus: getCssVar('--tuna-palette-action-focus'),
    },
    background: {
      default: getCssVar('--tuna-palette-background-default'),
      paper: getCssVar('--tuna-palette-background-paper'),
    },
    divider: getCssVar('--tuna-palette-divider'),
    layers: getCssVar('--tuna-palette-layers'),
    grey: Object.fromEntries(
      ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => [
        shade,
        getCssVar(`--tuna-palette-grey-${shade}`),
      ])
    ),
  },
  notes: {
    main: getCssVar('--tuna-notes-main'),
    outOfScale: getCssVar('--tuna-notes-outOfScale'),
    inScale: getCssVar('--tuna-notes-inScale'),
    root: getCssVar('--tuna-notes-root'),
  },
  spacing,
  breakpoints,
  shape: {
    borderRadius: getCssVar('--tuna-shape-borderRadius'),
  },
})

/**
 * Hook to access design tokens in components.
 *
 * Prefer using CSS custom properties directly in styles when possible.
 * Use this hook only when you need values in JS (canvas, SVG, calculations).
 */
export const useDesignTokens = (): DesignTokens => getDesignTokens()
